import type { CheckboxField, DateField, FieldType, FormulaField } from '@lighthouse/core'
import cls from 'classnames'
import React, { useMemo } from 'react'

import {
    isAggregationValue,
    isCheckboxValue,
    isDateListValue,
    isFileValue,
    isFormulaValue,
    isIdsValue,
    isNumberListValue,
    isRichTextValue,
    isSimpleValue
} from '../../utils/helper'
import { AggregationValuePreviewer } from '../FieldValuePreviewer/AggregationValuePreviewer'
import { DepartmentValuePreviewer } from '../FieldValuePreviewer/DepartmentValuePreviewer'
import { FormulaValuePreviewer } from '../FieldValuePreviewer/FormulaValuePreviewer'
import { PersonValuePreviewer } from '../FieldValuePreviewer/PersonValuePreviewer'
import { RoleValuePreviewer } from '../FieldValuePreviewer/RoleValuePreviewer'
import { SingleSelectValuePreviewer } from '../FieldValuePreviewer/SingleSelectValuePreviewer'
import { VideoValuePreviewer } from '../FieldValuePreviewer/VideoValuePreviewer'
import { Placeholder } from '../PlaceHolder'
import { CheckboxValuePreviewer } from './CheckboxValuePreviewer'
import { DateValuePreviewer } from './DateValuePreviewer'
import { FileValuePreviewer } from './FileValuePreviewer'
import { IdValuePreviewer } from './IdValuePreviewer'
import { NotesValuePreviewer } from './NotesValuePreviewer'
import { NumberValuePreviewer } from './NumberValuePreviewer'
import { SelectValuePreviewer } from './SelectValuePreviewer'
import { SimpleTextValuePreviewer } from './SimpleTextValuePreviewer'
import * as SC from './styles'
import { TextValuePreviewer } from './TextValuePreviewer'
import type { ViewFieldValuePreviewerProps } from './types'

// 以下字段类型需要单独处理垂直居中
const SPECIAL_FIELD_TYPE = new Set<Partial<FieldType>>(['file', 'select'])

const ViewFieldValuePreviewer: React.FC<ViewFieldValuePreviewerProps> = ({
    field,
    data,
    isWrap,
    previewHost = 'table',
    enableEdit = false,
    direction,
    width,
    itemClickable = true,
    placeholder,
    styleConditions,
    isBold,
    disabled,
    onCellChange
}) => {
    const isPreviewAsCell = previewHost === 'table' || previewHost === 'field'
    const valueContent = useMemo(() => {
        const { type } = field
        if (placeholder && !data && data !== 0) {
            const isSpecialField = SPECIAL_FIELD_TYPE.has(type)
            return <Placeholder style={isSpecialField ? { marginTop: 3 } : {}} value={placeholder} />
        }

        switch (type) {
            case 'url':
            case 'phoneNumber':
            case 'email': {
                return (
                    <SimpleTextValuePreviewer
                        field={field}
                        data={isSimpleValue(data) ? data : ''}
                        isWrap={isWrap}
                        width={width}
                        styleConditions={styleConditions}
                        isBold={isBold}
                    />
                )
            }
            case 'id': {
                return (
                    <IdValuePreviewer
                        field={field}
                        data={isSimpleValue(data) ? data : ''}
                        isWrap={isWrap}
                        width={width}
                        styleConditions={styleConditions}
                        isBold={isBold}
                    />
                )
            }
            case 'text': {
                return (
                    <TextValuePreviewer
                        field={field}
                        data={isSimpleValue(data) ? data : undefined}
                        width={width}
                        isWrap={isWrap}
                        styleConditions={styleConditions}
                        isBold={isBold}
                    />
                )
            }
            case 'select': {
                const newData = isIdsValue(data) ? data : []
                if (placeholder && newData.length === 0) {
                    return <Placeholder style={{ marginTop: 3 }} value={placeholder} />
                }
                return (
                    <SelectValuePreviewer
                        field={field}
                        data={newData}
                        width={width}
                        direction={direction}
                        isWrap={isWrap}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'singleSelect': {
                const newData = isIdsValue(data) ? data : []
                if (placeholder && newData.length === 0) {
                    return <Placeholder style={{ marginTop: 3 }} value={placeholder} />
                }
                return (
                    <SingleSelectValuePreviewer
                        direction={direction}
                        field={field}
                        width={width}
                        data={newData}
                        isWrap={isWrap}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'date': {
                return (
                    <DateValuePreviewer
                        field={field}
                        data={isDateListValue(data) ? data : undefined}
                        isWrap={isWrap}
                        width={width}
                        styleConditions={styleConditions}
                        isBold={isBold}
                    />
                )
            }
            case 'number': {
                return (
                    <NumberValuePreviewer
                        field={field}
                        data={isNumberListValue(data) ? data : undefined}
                        isWrap={isWrap}
                        width={width}
                        styleConditions={styleConditions}
                        isBold={isBold}
                    />
                )
            }
            case 'person': {
                return (
                    <PersonValuePreviewer
                        field={field}
                        data={isIdsValue(data) ? data : []}
                        isWrap={isWrap}
                        width={width}
                        direction={direction}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'file': {
                const newData = isFileValue(data) ? data : []
                if (placeholder && newData.length === 0) {
                    return <Placeholder style={{ marginTop: 3 }} value={placeholder} />
                }
                return (
                    <FileValuePreviewer
                        data={newData}
                        isWrap={isWrap}
                        width={width}
                        itemClickable={itemClickable}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'video': {
                const newData = isFileValue(data) ? data : []
                if (placeholder && newData.length === 0) {
                    return <Placeholder style={{ marginTop: 3 }} value={placeholder} />
                }
                return (
                    <VideoValuePreviewer
                        field={field}
                        data={newData}
                        isWrap={isWrap}
                        width={width}
                        enableEdit={enableEdit}
                        itemClickable={itemClickable}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'role': {
                return (
                    <RoleValuePreviewer
                        field={field}
                        data={isIdsValue(data) ? data : []}
                        direction={direction}
                        width={width}
                        isWrap={isWrap}
                        styleConditions={styleConditions}
                    />
                )
            }

            case 'formula': {
                return (
                    <FormulaValuePreviewer
                        field={field}
                        width={width}
                        data={isFormulaValue(data) ? data : ''}
                        isWrap={isWrap}
                        isBold={isBold}
                        styleConditions={styleConditions}
                    />
                )
            }
            case 'checkbox': {
                return (
                    <CheckboxValuePreviewer
                        field={field}
                        data={isCheckboxValue(data) ? data : false}
                        styleConditions={styleConditions}
                        disabled={disabled}
                        onCellChange={onCellChange}
                    />
                )
            }
            case 'notes': {
                return <NotesValuePreviewer field={field} data={isRichTextValue(data) ? data : {}} styleConditions={styleConditions} />
            }
            case 'parentDepartment':
            case 'department': {
                return (
                    <DepartmentValuePreviewer
                        direction={direction}
                        field={field}
                        data={isIdsValue(data) ? data : []}
                        isWrap={isWrap}
                        width={width}
                    />
                )
            }
            case 'aggregation': {
                return (
                    <AggregationValuePreviewer
                        field={field}
                        data={isAggregationValue(data) ? data : ''}
                        isWrap={isWrap}
                        width={width}
                        isBold={isBold}
                        styleConditions={styleConditions}
                    />
                )
            }
            default: {
                return <></>
            }
        }
    }, [field, placeholder, data, isWrap, width, isBold, styleConditions, direction, itemClickable, enableEdit, disabled, onCellChange])
    return isPreviewAsCell ? (
        <SC.ValueContainer className={cls('overflow', { 'enable-edit': enableEdit })}>{valueContent}</SC.ValueContainer>
    ) : (
        <SC.ValuePreview>{valueContent}</SC.ValuePreview>
    )
}

export default ViewFieldValuePreviewer
